
import { Download, Check, Chrome, Shield, Sparkles, ArrowRight, FileText } from "lucide-react";
import CTAButton from "./CTAButton";

interface HeroProps {
  className?: string;
}

const Hero = ({
  className = ""
}: HeroProps) => {
  const userAgent = navigator.userAgent.toLowerCase();
  const isFirefox = userAgent.includes('firefox');
  const isEdge = userAgent.includes('edg/');

  const openStore = () => {
    if (isFirefox) {
      window.location.href = "https://addons.mozilla.org/firefox/downloads/file/4529261/pdf_search_helper-1.0.0.xpi";
      return;
    }
    const url = "https://chromewebstore.google.com/detail/pdf-search-helper/bficdbgmiiecnmoldiffehkhcafobnkl";
    const width = window.screen.width;
    const height = window.screen.height;
    window.open(url, "ExtensionStore", `width=${width},height=${height},left=0,top=0`);
  };

  const getBrowserName = () => {
    if (isFirefox) return "Firefox";
    if (isEdge) return "Edge";
    return "Chrome";
  };

  const buttonClassName = className?.includes("green-cta") ? "bg-green-600 hover:bg-green-700" : "bg-blue-600 hover:bg-blue-700";

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-100/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute top-3/4 right-1/4 w-80 h-80 bg-indigo-100/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-cyan-50/30 rounded-full blur-3xl"></div>
      </div>

      {/* Subtle grid pattern overlay */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(0,0,0,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.02)_1px,transparent_1px)] bg-[size:4rem_4rem]"></div>

      {/* Logo positioned at top left */}
      <div className="absolute top-6 left-8 z-50">
        <a href="https://www.searchsafesecurity.com" target="_blank" rel="noopener noreferrer" className="block hover:scale-105 transition-all duration-300">
          <img className="h-16 w-auto" src="/lovable-uploads/522d17fa-541b-46b2-aec6-aec8edf40b37.png" alt="PDF Search Helper" loading="eager" />
        </a>
      </div>

      {/* Main content */}
      <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-4 text-center">
        <div className="max-w-5xl mx-auto">
          
          {/* Main headline */}
          <h1 className="text-5xl md:text-7xl font-black bg-gradient-to-r from-gray-900 via-blue-800 to-indigo-800 bg-clip-text text-transparent mb-6 leading-tight tracking-tight">
            Find PDFs
            <br />
            <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              Instantly
            </span>
          </h1>
          
          {/* Subtitle */}
          <p className="text-xl md:text-2xl text-gray-700 mb-4 max-w-3xl mx-auto font-light leading-relaxed">
            Install the free <span className="font-semibold text-gray-900">PDF Search Helper</span> extension for {getBrowserName()}.
          </p>
          
          <p className="text-lg text-gray-600 mb-12 max-w-2xl mx-auto">
            Automatically search for PDF files using Yahoo! Just type "PDF" with your search term and get filtered results in a new tab.
          </p>

          {/* CTA Section */}
          <div className="space-y-8">
            {/* Primary CTA */}
            <div className="flex justify-center">
              <CTAButton 
                onClick={openStore} 
                size="large" 
                className={`${buttonClassName} group relative overflow-hidden px-12 py-6 text-2xl font-bold shadow-2xl hover:shadow-blue-500/25 transform hover:-translate-y-1 transition-all duration-300 border-2 border-blue-400/20`}
              >
                <div className="flex items-center gap-4">
                  <Chrome className="w-8 h-8" />
                  <span>Add to {getBrowserName()}</span>
                  <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform duration-300" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
              </CTAButton>
            </div>

            {/* Trust indicators */}
            <div className="flex flex-wrap justify-center gap-8 pt-8">
              <div className="flex items-center gap-3 text-emerald-600">
                <div className="w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center">
                  <Check className="w-4 h-4 text-white" />
                </div>
                <span className="font-medium">Yahoo! Powered</span>
              </div>
              <div className="flex items-center gap-3 text-blue-600">
                <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                  <FileText className="w-4 h-4 text-white" />
                </div>
                <span className="font-medium">PDF Focused</span>
              </div>
              <div className="flex items-center gap-3 text-indigo-600">
                <div className="w-6 h-6 bg-indigo-500 rounded-full flex items-center justify-center">
                  <Chrome className="w-4 h-4 text-white" />
                </div>
                <span className="font-medium">Browser Compatible</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Hero;
